/**
 * Demolition Lab — material destruction profiles (M1 / D1 / D2.2).
 *
 * D2.2: physical transfer function lives in `physics` (strength, fracture energy,
 * brittleness, damage accum). Response records / damageRadius / authored piece
 * counts are gone — the resolver computes carve + fragmentation from energy.
 */

import { FRACTURE_RNG_SEED } from './fracture/voronoiFracture';
import {
  MATERIAL_PHYSICS,
  type MaterialPhysics,
} from './damageModel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProfileId = 'concrete' | 'brick' | 'wood' | 'stone' | 'glass' | 'plaster';

export interface MaterialParams {
  color: string;
  roughness: number;
  metalness?: number;
  /** Glass is the only material allowed transparency. */
  transparent?: boolean;
  opacity?: number;
  /** Always true for glass shards so sorting stays acceptable. */
  depthWrite?: boolean;
}

/** Load-time non-uniform Voronoi (concrete / stone). */
export interface NonUniformSeedSpec {
  kind: 'nonUniform';
  jitter: number;
  minSpacing: number;
  maxSpacing: number;
  zLayers: number;
  seed: number;
}

/** Masonry courses + running bond (brick). */
export interface BrickCourseSeedSpec {
  kind: 'brickCourse';
  courseHeight: number;
  brickLength: number;
  /** Tiny positional jitter so mortar lines read hand-laid. */
  jitter: number;
  seed: number;
}

/** Anisotropic face grid (wood grain along X). */
export interface AnisotropicSeedSpec {
  kind: 'anisotropic';
  minSpacingX: number;
  maxSpacingX: number;
  minSpacingY: number;
  maxSpacingY: number;
  zLayers: number;
  jitter: number;
  seed: number;
}

/**
 * 2.5D radial spiderweb — generated AT IMPACT for glass.
 * `rings` / `spokes` are nominal; generator densifies near center.
 */
export interface RadialShatterSeedSpec {
  kind: 'radialShatter';
  rings: number;
  spokes: number;
  jitter: number;
  seed: number;
}

export type SeedSpec =
  | NonUniformSeedSpec
  | BrickCourseSeedSpec
  | AnisotropicSeedSpec
  | RadialShatterSeedSpec;

/** Contact surface feel for dynamic chunks (restitution / friction / damping). */
export interface SurfaceResponse {
  restitution: number;
  friction: number;
  linearDamping: number;
  angularDamping: number;
}

export interface DestructionProfile {
  id: ProfileId;
  label: string;
  /** Full wall dims (m). Glass is a thin pane. */
  wall: { w: number; h: number; t: number };
  /** glass = 'impact' — partition built on first qualifying hit. */
  partitionTiming: 'load' | 'impact';
  seedSpec: SeedSpec;
  density: number;
  /** Minimum contact approach speed (m/s) to emit a kinetic DamageEvent. */
  impactSpeedThreshold: number;
  /**
   * Sub-fracture physics-body budget per shot (hard cap). Piece count EMERGES
   * from the energy budget up to this cap. Blast/volley events still honor it;
   * BLAST_VOLLEY_SUB_BUDGET is only the remaining shared ceiling across a
   * multi-charge volley, not a replacement for this identity.
   */
  subFractureBudget: number;
  sizeFloorVol: number;
  /** Launch upward bias (omni / focused blend). Scale is energy-derived now. */
  impulse: { upwardBias: number };
  proxyShrink: number;
  /** Dynamic chunk contact / damping feel. */
  surface: SurfaceResponse;
  /**
   * Crush-to-powder. Coefficient 0 = splinters/shards only (wood, glass).
   * Threshold sits at or just above `sizeFloorVol` so default-power shots
   * take powder from already-invisible fines first.
   */
  comminution: {
    coefficient: number;
    pulverizeSizeThreshold: number;
  };
  /** Physical transfer function (D2.2). */
  physics: MaterialPhysics;
  /**
   * Multiplier on the sticky charge's energy for THIS material only (kinetic
   * weapons, concussion, and blast RADIUS/geometry are untouched). Exists
   * because BOMB_ENERGY_J was calibrated once, against concrete, and every other
   * material's very different strength/cell-volume table was never checked
   * against it — brick/wood over-carved, stone usually did nothing at all.
   * 1 = unscaled (concrete is the anchor; do not change its measured behavior).
   */
  chargeEnergyScale: number;
  skin: { outer: MaterialParams; interior: MaterialParams };
}

// ---------------------------------------------------------------------------
// Profiles
// ---------------------------------------------------------------------------

/** Concrete — wall 6×4.2×0.6, non-uniform, density 2300. */
export const CONCRETE_PROFILE: DestructionProfile = {
  id: 'concrete',
  label: 'Concrete',
  wall: { w: 6.0, h: 4.2, t: 0.6 },
  partitionTiming: 'load',
  seedSpec: {
    kind: 'nonUniform',
    jitter: 0.4,
    minSpacing: 0.28,
    maxSpacing: 1.2,
    zLayers: 2,
    seed: FRACTURE_RNG_SEED,
  },
  density: 2300,
  impactSpeedThreshold: 8,
  subFractureBudget: 48,
  sizeFloorVol: 0.003,
  impulse: { upwardBias: 0.15 },
  proxyShrink: 0.88,
  surface: { restitution: 0.05, friction: 0.85, linearDamping: 0.05, angularDamping: 0.15 },
  comminution: { coefficient: 0.2, pulverizeSizeThreshold: 0.0032 },
  physics: { ...MATERIAL_PHYSICS.concrete },
  /** Anchor: BOMB_ENERGY_J was calibrated against this profile — leave at 1. */
  chargeEnergyScale: 1,
  skin: {
    outer: { color: '#9a958c', roughness: 0.92, metalness: 0.04 },
    interior: { color: '#b5aea2', roughness: 0.95, metalness: 0 },
  },
};

/** Brick — coursed masonry, running bond, red outer / grey mortar core. */
export const BRICK_PROFILE: DestructionProfile = {
  id: 'brick',
  label: 'Brick',
  wall: { w: 6.0, h: 4.2, t: 0.3 },
  partitionTiming: 'load',
  seedSpec: {
    kind: 'brickCourse',
    courseHeight: 0.21,
    brickLength: 0.45,
    jitter: 0.01,
    seed: FRACTURE_RNG_SEED ^ 0xb21c,
  },
  density: 1800,
  impactSpeedThreshold: 8,
  subFractureBudget: 36,
  sizeFloorVol: 0.002,
  impulse: { upwardBias: 0.12 },
  proxyShrink: 0.9,
  surface: { restitution: 0.05, friction: 0.8, linearDamping: 0.05, angularDamping: 0.15 },
  comminution: { coefficient: 0.35, pulverizeSizeThreshold: 0.0024 },
  physics: { ...MATERIAL_PHYSICS.brick },
  /** Calibrated: centre bomb ~0.45 carved vol (band 0.35–0.55); ≥ concrete. */
  chargeEnergyScale: 0.335,
  skin: {
    outer: { color: '#9a4a35', roughness: 0.9, metalness: 0.02 },
    interior: { color: '#8a8680', roughness: 0.95, metalness: 0 },
  },
};

/** Wood — long thin planks (grain along X), light, fly on impact. */
export const WOOD_PROFILE: DestructionProfile = {
  id: 'wood',
  label: 'Wood',
  wall: { w: 6.0, h: 4.2, t: 0.12 },
  partitionTiming: 'load',
  seedSpec: {
    kind: 'anisotropic',
    minSpacingX: 1.0,
    maxSpacingX: 1.6,
    minSpacingY: 0.1,
    maxSpacingY: 0.16,
    zLayers: 1,
    jitter: 0.1,
    seed: FRACTURE_RNG_SEED ^ 0x70ed,
  },
  density: 600,
  impactSpeedThreshold: 6,
  subFractureBudget: 32,
  sizeFloorVol: 0.0008,
  impulse: { upwardBias: 0.22 },
  proxyShrink: 0.85,
  surface: { restitution: 0.12, friction: 0.6, linearDamping: 0.04, angularDamping: 0.1 },
  comminution: { coefficient: 0, pulverizeSizeThreshold: 0.0008 },
  physics: {
    ...MATERIAL_PHYSICS.wood,
    anisotropy: MATERIAL_PHYSICS.wood.anisotropy
      ? { ...MATERIAL_PHYSICS.wood.anisotropy, axis: [1, 0, 0] }
      : undefined,
  },
  /** Calibrated: centre bomb ~0.48 carved vol (band 0.38–0.55); ≥ brick. */
  chargeEnergyScale: 0.475,
  skin: {
    outer: { color: '#8b5a2b', roughness: 0.85, metalness: 0.02 },
    interior: { color: '#d4c4a8', roughness: 0.9, metalness: 0 },
  },
};

/** Stone — few big boulders, high strength, rare sub-fracture. */
export const STONE_PROFILE: DestructionProfile = {
  id: 'stone',
  label: 'Stone',
  wall: { w: 6.0, h: 4.2, t: 0.7 },
  partitionTiming: 'load',
  seedSpec: {
    kind: 'nonUniform',
    jitter: 0.35,
    minSpacing: 0.7,
    maxSpacing: 1.4,
    zLayers: 1,
    seed: FRACTURE_RNG_SEED ^ 0x5704e,
  },
  density: 2700,
  impactSpeedThreshold: 12,
  subFractureBudget: 12,
  sizeFloorVol: 0.008,
  impulse: { upwardBias: 0.05 },
  // Slightly tighter proxies so base-blast boulder centroids rest under 1.25m.
  // Hull path uses CHUNK_HULL_SHRINK; this applies to the box fallback only.
  proxyShrink: 0.82,
  // Boulders THUD and stop — no visible bounce.
  surface: { restitution: 0.01, friction: 0.98, linearDamping: 0.12, angularDamping: 0.45 },
  comminution: { coefficient: 0.06, pulverizeSizeThreshold: 0.008 },
  physics: { ...MATERIAL_PHYSICS.stone },
  /** Calibrated: centre bomb ~0.12 carved vol (band 0.08–0.18). */
  chargeEnergyScale: 1.85,
  skin: {
    outer: { color: '#6a6e74', roughness: 0.88, metalness: 0.05 },
    interior: { color: '#9aa0a8', roughness: 0.92, metalness: 0 },
  },
};

/**
 * Glass — thin pane; stays monolith until first hit, then radial spiderweb
 * partition at impact. Full-pane release EMERGES from low tensile + high
 * brittleness via the resolver (no fullRelease flag).
 */
export const GLASS_PROFILE: DestructionProfile = {
  id: 'glass',
  label: 'Glass',
  wall: { w: 5.0, h: 3.2, t: 0.05 },
  partitionTiming: 'impact',
  seedSpec: {
    kind: 'radialShatter',
    rings: 6,
    spokes: 14,
    jitter: 0.12,
    seed: FRACTURE_RNG_SEED ^ 0x91a55,
  },
  density: 2500,
  impactSpeedThreshold: 5,
  subFractureBudget: 0,
  sizeFloorVol: 0.00015,
  impulse: { upwardBias: 0.05 },
  proxyShrink: 0.92,
  surface: { restitution: 0.18, friction: 0.35, linearDamping: 0.03, angularDamping: 0.06 },
  comminution: { coefficient: 0, pulverizeSizeThreshold: 0.00015 },
  physics: { ...MATERIAL_PHYSICS.glass },
  /** Full-pane release is already glass's correct identity — leave at 1. */
  chargeEnergyScale: 1,
  skin: {
    outer: {
      color: '#a8d4d0',
      roughness: 0.12,
      metalness: 0.15,
      transparent: true,
      opacity: 0.55,
      depthWrite: true,
    },
    interior: {
      color: '#c0e0dc',
      roughness: 0.18,
      metalness: 0.1,
      transparent: true,
      opacity: 0.55,
      depthWrite: true,
    },
  },
};

/**
 * Plaster — clone of concrete's non-uniform Voronoi wall with weaker, crumblier
 * transfer numbers. Density 1200 kg/m³ (gypsum ~850–1600) vs concrete 2300;
 * compressive 8 MPa vs 30; smaller seed spacing so the pane breaks into finer
 * chips. chargeEnergyScale 0.55 because the bomb was calibrated on concrete.
 */
export const PLASTER_PROFILE: DestructionProfile = {
  id: 'plaster',
  label: 'Plaster',
  wall: { w: 6.0, h: 4.2, t: 0.35 },
  partitionTiming: 'load',
  seedSpec: {
    kind: 'nonUniform',
    jitter: 0.38,
    minSpacing: 0.16,
    maxSpacing: 0.7,
    zLayers: 2,
    seed: FRACTURE_RNG_SEED ^ 0x91a57,
  },
  density: 1200,
  impactSpeedThreshold: 6,
  subFractureBudget: 40,
  sizeFloorVol: 0.002,
  impulse: { upwardBias: 0.18 },
  proxyShrink: 0.88,
  surface: { restitution: 0.04, friction: 0.78, linearDamping: 0.06, angularDamping: 0.16 },
  comminution: { coefficient: 0.42, pulverizeSizeThreshold: 0.0022 },
  physics: { ...MATERIAL_PHYSICS.plaster },
  chargeEnergyScale: 0.55,
  skin: {
    outer: { color: '#d4cdc2', roughness: 0.82, metalness: 0 },
    interior: { color: '#efe8dc', roughness: 0.94, metalness: 0 },
  },
};

export const DESTRUCTION_PROFILES: Record<ProfileId, DestructionProfile> = {
  concrete: CONCRETE_PROFILE,
  brick: BRICK_PROFILE,
  wood: WOOD_PROFILE,
  stone: STONE_PROFILE,
  glass: GLASS_PROFILE,
  plaster: PLASTER_PROFILE,
};

export const PROFILE_IDS: ProfileId[] = ['concrete', 'brick', 'wood', 'stone', 'glass', 'plaster'];

export function getDestructionProfile(id: string): DestructionProfile {
  const p = DESTRUCTION_PROFILES[id as ProfileId];
  if (!p) return CONCRETE_PROFILE;
  return p;
}

export function isProfileId(id: string): id is ProfileId {
  return id in DESTRUCTION_PROFILES;
}

/** Half-extents from a profile wall. */
export function profileWallHalf(p: DestructionProfile) {
  return { x: p.wall.w / 2, y: p.wall.h / 2, z: p.wall.t / 2 };
}

/** World-space wall center (bottom on y=0). */
export function profileWallCenter(p: DestructionProfile) {
  return { x: 0, y: p.wall.h / 2, z: 0 };
}

/**
 * Assemble surface spawn options for a dynamic chunk (unit-testable; does not
 * touch wasm). Used by spawnCellBody for damping / friction / restitution.
 */
export function surfaceSpawnOptions(profile: DestructionProfile): SurfaceResponse {
  return { ...profile.surface };
}
